/**
 * @file CV game main module
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2404.20.001
 */

/** @module main */

import { handleExternalLinks } from "./externalLinksHandler.js";
import { ModeSwitch } from "./ModeSwitch.js";
import { Game } from "./Game.js";
import { initialiseOptionsControls } from "./gameOptions.js";
import { GameInteractivesModal } from "./gameInteractivesModal.js";
import { MiniGameLoader } from "./MiniGameLoader.js";
import { initialisePlayerSelector } from "./gamePlayerSelect.js";
import { GameTouchControls } from "./gameTouchControls.js";

// #region Update document class

document.documentElement.classList.remove( "no-js" );

// Detect touch capabilities
if (
	( "ontouchstart" in window ) 
|| ( navigator.maxTouchPoints > 0 ) 
|| ( navigator.msMaxTouchPoints > 0 )
) {
	document.documentElement.classList.add( "has-touch-capabilites" );
}

// #endregion Update document class

handleExternalLinks( document );

/**
 * The game instance.
 * @type {module:game~Game}
 */
const game = new Game( 
	document.querySelector( ".game .screen > canvas" ), 
	{ width: 64, height: 64 } 
);

new ModeSwitch( game, document.querySelector( "body > menu" ) );

await game.loadData( {
	main: "data/cv01.screen.json",
	interior02: "data/cv01.interior02.screen.json",
	interior03: "data/cv01.interior03.screen.json",
	interior04: "data/cv01.interior04.screen.json",
	interior05: "data/cv01.interior05.screen.json",
	interior06: "data/cv01.interior06.screen.json"
} );
game.loadScreen( "main" );
game.drawFrame( 0 );

initialiseOptionsControls( game, document.querySelector( ".options" ) );

/**
 * A document's `dialog` element, with `modal` and `interactives` classes.
 * @type {HTMLDialogElement}
 */
const interactivesModal = document.querySelector( "dialog.modal.interactives" );

const gameInteractivesModal = new GameInteractivesModal( 
game, interactivesModal 
);

// #region PaddleAndBallGame mini-game

const miniGameLoader = new MiniGameLoader(
	game,
	document.querySelector( "aside.mini-game" ),
	gameInteractivesModal
);

const contentFilter = miniGameLoader.bindPaddleAndBallGameLaunchers;
const boundContentFilter = contentFilter.bind( miniGameLoader );
gameInteractivesModal.filterLoadedContent = boundContentFilter;

// #endregion PaddleAndBallGame mini-game

// #region Expand details for print media

window.matchMedia( "print" ).addEventListener( "change", function( changeEvent ) {
	if ( changeEvent.matches ) {
		const details = document.body.querySelectorAll( "details:not([open])" );
		for ( let detail of details ) {
			detail.setAttribute( "open", "" );
			detail.dataset.wasclosed = "";
		}
		return;
	} 
	const details = document.body.querySelectorAll("details[data-wasclosed]");
	for ( let detail of details ) {
		detail.removeAttribute( "open" );
		delete detail.dataset.wasclosed;
	}
});

// #endregion Expand details for print

// #region Show game "start-up"

const gameScreenOverlay = document.querySelector( ".game .screen .overlay" );

// Grab text from intro
const introClone = document.querySelector( "#intro" ).cloneNode( true );
// Convert non-external links to text.
const anchors = introClone.querySelectorAll( "a:not([rel*=external])" );
for( let a = anchors.length - 1; a >= 0; a = a - 1 ) {
	introClone.innerHTML = introClone.innerHTML.replace(
		anchors[ a ].outerHTML,
		anchors[ a ].innerHTML
	);
}
const welcomeHTML = ""
	+ "<header><h2 class=\"title\">Welcome</header>"
	+ introClone.innerHTML
;

gameInteractivesModal.keepGamePausedOnClose = true;
interactivesModal.classList.add( "intro" );
gameInteractivesModal.show( welcomeHTML, "welcome" );

gameScreenOverlay.querySelector( ".loading" ).classList.remove( "visible" );

const playerChoice = gameScreenOverlay.querySelector( ".player-choice" );
playerChoice.classList.add( "visible" );
game.playerSelectorIsVisible = true;
playerChoice.querySelector( "input:checked" ).focus();
initialisePlayerSelector( gameScreenOverlay, game );

const overlayStepButtons = gameScreenOverlay.querySelectorAll( ".step button" );

for( let b = overlayStepButtons.length - 1; b >= 0; b = b - 1 ) {
	const button = overlayStepButtons[ b ];
	button.addEventListener( "click", function( clickEvent ) {
		clickEvent && clickEvent.preventDefault && clickEvent.preventDefault();
		const visibleStep = gameScreenOverlay.querySelector( ".step.visible" );
		const targetClassList = clickEvent.target.classList;
		const clickedNext = targetClassList.contains( "next" );
		const clickedPrev = targetClassList.contains( "prev" );
		if ( clickedNext || clickedPrev ) {
			const showStep = clickedNext 
				? visibleStep.nextElementSibling 
				: visibleStep.previousElementSibling
			;
			showStep.classList.add( "visible" );
			visibleStep.classList.remove( "visible" );
			game.playerSelectorIsVisible = showStep.classList.contains( 
				"player-choice" 
			);
		} else if ( targetClassList.contains( "start" ) ) {
			game.playerSelectorIsVisible = false;
			gameScreenOverlay.parentElement.removeChild( gameScreenOverlay );
			gameInteractivesModal.keepGamePausedOnClose = false;
			game.pause( false );
		}
		
	} );
}

// #endregion Show game "start-up"

if ( "ontouchstart" in document.documentElement ) {
	document.documentElement.classList.add( "has-touch-capabilities" );
}

new GameTouchControls( 
	game, document.querySelector( ".game .touch-controls" ), playerChoice 
);